import React, { useEffect, useMemo, useRef, useState } from 'react';
import CarouselComponent from '../../../Misc/CarouselComponent/CarouselComponent';
import styles from './styles.module.scss';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import { BarLoader, PuffLoader, PulseLoader, RiseLoader, PacmanLoader } from 'react-spinners';

import errorMsg from '../../../Functions/errorMsg';
import successMsg from '../../../Functions/successMsg';

import { faAd, faBook, faBookmark, faCheck, faCheckCircle, faEye, faFlag, faMessage, faPaperPlane, faShare, faStar, faXmark, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import openAdManager from '../../../Functions/openAdManager';

import Image from 'next/image';
import { AnimatePresence, useInView } from 'framer-motion';
import { useRouter } from 'next/router';

import { motion } from 'framer-motion';
import MapWithRadius from '../../../Misc/MapWithRadius/MapWithRadius';

import NumberFormat from 'react-number-format';
import { useSocket } from '../../../Context/socket';
import { isEqual } from 'lodash';
import OnlineProfile from '../../UserProfile/OnlineProfile/OnlineProfile';

export default function AuctionListing({ listingID, closeListing }) {

    const { data: session, status } = useSession();
    const { socket, socketConnected } = useSocket();

    const router = useRouter();

    const [initialLoading, setInitialLoading] = useState(null);

    const [data, setData] = useState(null);
    const [posterProfile, setPosterProfile] = useState(null);
    const [reviewData, setReviewData] = useState(null);

    const [reported, setReported] = useState(false);

    useEffect(() => {
      if(initialLoading !== null) return;
      if(data !== null) return;
      if(status === 'loading') return;
  
      const requestingUser = status === 'unauthenticated' ? 'nouser' : session.user.token.username;
  
      async function getListingData() {
          setInitialLoading(true);
  
          const result = await axios.post(`${window.location.origin}/api/auctionHub/getAuction`, {
              listingID: listingID,
              editData: false,
              requestingUser: requestingUser
          }).catch((error) => {
              errorMsg(error?.response?.data?.status || error.toString())
              closeListing(null);
              setInitialLoading(false);
          })
  
          if(result && result.status === 200) {
              const listingData = result.data;
              setData(listingData.listing);
              setPosterProfile(listingData.profile);
              setReviewData(returnRating(listingData.listing.reviews));
              setReported(listingData.listing.reports.some((report) => report.reportingUser === requestingUser))
          }
  
          setInitialLoading(false);
      }
  
      getListingData();
  
  }, [session, status])

    function isLoading() {
        if(status === 'loading') return true;
        if(initialLoading === true || initialLoading === null) return true;
        if(data === null) return true;
        if(posterProfile === null) return true;
        if(reviewData === null) return true;
        return false;
    }

    function authenticationIsOwnerCheck() {
      if(!session) return false;
      if(status !== 'authenticated') return false;
      if(session.user.token.username === data.owner) return false;
      return true;
    }
    
    const {displayTime, displayTimeType} = createdTimeStamp(data?.creationDate);
    
    const memoizeCarousel = useMemo(() => {
      if (data !== null && data.images.length > 0) {
        return <CarouselComponent listingImages={data.images} />;
      } else {
        return null;
      }
    }, [data]);

    function isAcceptingPayments() {
      if(status === 'loading') return false;
      return data.acceptPayments.enabled;
    }

    function userAuthenticated() {
      if(!session) return false;
      if(status !== 'authenticated') return false;
      if(!session.user.token.id) return false;
      return true;
    }

    const memoizeOptionBar = useMemo(() => <OptionBar listing={data} session={session} closeListing={closeListing} authenticationIsOwnerCheck={authenticationIsOwnerCheck} handleCreateReport={() => setCreateReport({listingID: data._id, reportingUser: data.owner})} reported={reported} />, [data, session, closeListing, reported])

    const [profileLoaded, setProfileLoaded] = useState(false);
    const [viewUserProfile, openUserProfile] = useState(false);

    const [createReport, setCreateReport] = useState(null);

    const [loadingHasConvo, setLoadingHasConvo] = useState(false);
    const [hasConvo, setHasConvo] = useState(null);

    useEffect(() => {
        if(loadingHasConvo || hasConvo !== null) return;
        if(!session) return;
        if(status === 'loading') return;
        if(!data) return;
        if(!posterProfile) return;

        const controller = new AbortController();

        const profileUsername = posterProfile.username;

        async function getUserProfile() {
            setLoadingHasConvo(true);
            const result = await axios.post(`${window.location.origin}/api/messaging/hasActiveConvo`, {
                signal: controller.signal,
                requestingUserID: session.user.token.id,
                otherUser: profileUsername,
                convoType: 'Auctions',
                listingID: data._id
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString());
            })

            if(result && result.status === 200) {
                const data = result.data;
                setHasConvo({ hasConvo: data.hasConvo, convoID: data.convoID });
            }

            setLoadingHasConvo(false);
        }

        getUserProfile();

        return () => {
            controller.abort();
        }

    }, [status, posterProfile, data, posterProfile])

    const quickMessageTextRef = useRef(null);

    async function handleQuickMessageSend() {
        if(loadingHasConvo) return;

        if(!posterProfile) return errorMsg("There was an error sending your message, please try again later.");

        if(!session) return errorMsg("Please login to message this user.");
        if(status === 'loading') return;
        if(status === 'unauthenticated') return errorMsg("Please login to message this user.");

        if(!socket) return errorMsg("There was an error sending your message, please try again later.");
        if(!socketConnected) return errorMsg("There was an error sending your message, please try again later.");

        if(!quickMessageTextRef || !quickMessageTextRef.current) return;

        const messageText = quickMessageTextRef.current.value;

        if(messageText.length === 0) return errorMsg("Please enter a message before submitting.");
        if(messageText.length > 1000) return errorMsg("Your message is too long, please submit below 1000 characters.");

        const toUser = posterProfile.username;

        const listingID = data._id;

        const senderID = session.user.token.id;
        const sender = session.user.token.username;

        const basicConvoData = {
            type: 'Auctions',
            listingID: listingID,
            receiver: toUser,
            sender: sender,
            creationDate: new Date().toISOString()
        }

        setLoadingHasConvo(true);

        const result = await axios.post(`${window.location.origin}/api/messaging/create`, {
            conversationData: basicConvoData,
            messageText: messageText,
            senderID: senderID,
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
        });

        if(result && result.status === 200) {
            const newConvoData = result.data;

            const hasNewConvoData = {
                hasConvo: true,
                convoID: newConvoData._id
            }

            setHasConvo(hasNewConvoData)

            socket.emit('createNewConversation', newConvoData)

            successMsg("Your message was sent.");

        }

        setLoadingHasConvo(false);

    }

    function handleOpenCurrentConversation() {
        if(!hasConvo) return errorMsg("Could not find this conversation, please try again later");
        if(hasConvo.hasConvo === false) return errorMsg("You do not have a conversation with this user.");

        const { query } = router;

        const updatedQuery = { ...query, openConversationID: hasConvo.convoID };

        router.push({ pathname: router.pathname, query: updatedQuery });
    }

  return (
    <div id={styles.wrapper}>
        { viewUserProfile && <OnlineProfile profileUsername={posterProfile.username} closeProfile={() => openUserProfile(false)} /> }
        
        <AnimatePresence>
          { !reported && createReport && <CreateReportBox listingID={createReport.listingID} reportingUser={createReport.reportingUser} closeReport={() => setCreateReport(null)} successfulReport={() => setReported(true)} /> }
        </AnimatePresence>

        { isLoading() === true && <PulseLoader size={30} color='white' /> }
        { isLoading() === false && 
        <div id={styles.listing}>

            {memoizeOptionBar}

            <div id={styles.infoContainer}>

                <section id={styles.carouselContainer}>
                  {memoizeCarousel}
                  <div id={styles.listingInfoDots}>
                    <div style={{display: data.views.length === 0 ? 'none' : 'flex'}}>
                      <FontAwesomeIcon icon={faEye} id={styles.icon} />
                      <h1>{data.views.length}</h1>
                    </div>
                    <div style={{display: data.watching.length === 0 ? 'none' : 'flex'}}>
                      <FontAwesomeIcon icon={faBookmark} id={styles.icon} />
                      <h1>{data.watching.length}</h1>
                    </div>
                  </div>
                </section>

                <section id={styles.info}>
                    <h1>{data.title}</h1>
                    <h2>Starting Bid: ${data.price}</h2>
                    <h3>Auction created {displayTime} {displayTimeType}</h3>
                    <hr />
                    <BidContainer userAuthenticated={userAuthenticated} auctionData={data} />
                    <hr />
                    <p style={{ display: data.description.length === 0 ? 'none' : 'flex'}}>{data.description}</p>
                    <span><h6>Condition</h6> <h6>{data.condition}</h6></span>
                    <span><h6>Category</h6> <h6>{data.category}</h6></span>
                    <span><h6>Meetup Preference</h6> <h6>{data.meetupPref}</h6></span>
                    {
                      data.auctionDetails.map((set, index) => {
                          return (
                            <span key={index}><h6>{set.type}</h6> <h6>{set.detail}</h6></span>
                          )
                          
                      })
                    }
                    <hr />
                </section>

                <section id={styles.map}>
                  <MapWithRadius zipCode={data.zip} />
                </section>

                <hr />

                <section id={styles.posterProfile}>
                    <div>
                        <span style={{display: profileLoaded === false ? 'flex' : 'none'}}> <PuffLoader color='#154734' size={50} /> </span>
                        <Image sizes='auto auto' fill src={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} alt={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} onLoadingComplete={() => setProfileLoaded(true)} />
                    </div>
                    <div>
                        <h1>{posterProfile.displayName}</h1>
                          { reviewData > 0 && <section>
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 1 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 2 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 3 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 4 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon icon={faStar} color={reviewData === 5 ? '#FFB81C' : 'lightgrey'} />
                        </section> }
                    </div>
                    <div>
                        <button onClick={() => openUserProfile(true)}>
                            <FontAwesomeIcon icon={faEye} id={styles.view} />
                        </button>
                    </div>
                </section>

                <section style={{display: authenticationIsOwnerCheck() ? 'flex' : 'none' }} id={styles.quickMessage}>
                    { !hasConvo || loadingHasConvo ?
                    <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}><BarLoader width={200} height={5} color='white' /></span>
                    :
                    <>
                        <div>
                            <FontAwesomeIcon icon={faMessage} id={styles.icon} />
                            <h1>{ !hasConvo.hasConvo ? 'Quick Message' : 'View Conversation' }</h1>
                        </div>
                        <div>
                            { !hasConvo.hasConvo && <textarea ref={quickMessageTextRef} placeholder="Write this user a message..." /> }
                            <FontAwesomeIcon onClick={!hasConvo.hasConvo ? handleQuickMessageSend : handleOpenCurrentConversation} icon={!hasConvo.hasConvo ? faPaperPlane : faEye} id={styles.icon} />
                        </div>
                    </>
                    }

                </section>

                <AdvertisementSpot adID={"AUCTION_AD_SPOT_ID"} session={session} router={router} closeListing={closeListing} />
            </div>

        </div>
        }

    </div>
  )
}

const BidContainer = ({ userAuthenticated, auctionData }) => {
  const router = useRouter();

  const auctionID = auctionData._id.toString();

  const { auctionSocketConnected, auctionSocket } = useSocket();

  const { data: session, status } = useSession();

  const [timeStamps, setTimeStamps] = useState(getTimeStamp(parseInt(auctionData.auctionDuration), auctionData.creationDate));

  const [loadingBids, setLoadingBids] = useState(true);
  const [bids, setBids] = useState(auctionData.bids.length > 0 ? auctionData.bids.sort((a, b) => b.bid - a.bid) : []);

  function handleIncomingBid(data) {
    const incomingAuctionID = data.auctionID;
    if(incomingAuctionID !== auctionID) return;
    const username = data.username;
    const bid = data.bid;

    const newBid = { auctionID, username, bid };
    const updatedBids = [...bids, newBid];

    updatedBids.sort((a, b) => b.bid - a.bid);

    setBids(updatedBids);
  }

  useEffect(() => {
    async function setupBidCAD() {
      if (!auctionID) {
        return errorMsg("There was an error loading the bidding data for this auction, please try again later.");
      }
  
      try {
        const getAuctionBids = await axios.post(`${window.location.origin}/api/auctionHub/helpers/getAuctionBids`, {
          auctionID: auctionID,
        });
  
        if (getAuctionBids.status === 200) {
          const result = getAuctionBids.data;
          setBids(result);
        } else {
          setBids(false);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setBids(false);
      }

      setLoadingBids(false);
    }
  
    setupBidCAD();

  }, [auctionID]);

  useEffect(() => {
    if(!auctionSocket || !auctionSocketConnected) return;

      const auctionRoomID = auctionID;

      auctionSocket.emit('join-auction-room', { roomID: auctionRoomID });

    return () => {
      auctionSocket.emit('leave-auction-room', { roomID: auctionRoomID });
    };
  }, [auctionSocket, auctionSocketConnected]);

  // Handle incoming new bid to update values

  useEffect(() => {
    if(!auctionSocket || !auctionSocketConnected) return;

    auctionSocket.on('incomingBid', handleIncomingBid);

    return () => {
      auctionSocket.off('incomingBid');
    };
  }, [auctionSocket, auctionSocketConnected])

  const [userBid, setUserBid] = useState('');

  const [pendingBid, setPendingBid] = useState(false);

  async function submitBid(e) {
    e.preventDefault();
    if(!userAuthenticated()) return router.push(`/account/login`);
    if(pendingBid === true) return;
    if(userBid.length === 0) return errorMsg("Please enter your bid before submitting.");

    const submittedBid = userBid;

    const bidDataRaw = {
      auctionID: auctionID,
      bidID: null,
      userID: session.user.token.id,
      username: session.user.token.username,
      bid: submittedBid,
      created: new Date().toISOString()
    }

    setPendingBid(true);

    const submitBidResult = await axios.post(`${window.location.origin}/api/auctionHub/helpers/submitBid`, bidDataRaw).catch((error) => {
      errorMsg(error?.response?.data?.status || error.toString())
    });

    if(submitBidResult && submitBidResult.status === 200) {
      const verifiedBidData = submitBidResult.data;
      auctionSocket.emit('submitBid', { auctionID: verifiedBidData.auctionID, username: verifiedBidData.username, bid: verifiedBidData.bid })
      setUserBid('');
      successMsg("Your bid was submitted.");
    }

    setPendingBid(false);

  }

  const priceValueCap = (inputObj) => {
    const { value } = inputObj;
    if (value <= 100000000) return true;
    return false;
  };

  function getHighestBid() {
    if(bids.length === 0) return { bid: auctionData.price }
    const sortedBidsArray = [...bids].sort((a, b) => b.bid - a.bid);
    if(!isEqual(sortedBidsArray, bids)) {
      setBids(sortedBidsArray);
    }

    return sortedBidsArray[0]
  }

  useEffect(() => {
    if(!auctionData) return;

    const duration = parseInt(auctionData.auctionDuration);
    const create = auctionData.creationDate;

    const countdownInterval = setInterval(() => {

      const timestamp = getTimeStamp(duration, create);
      if(timestamp.status === 'Auction Closed' || timestamp.status === undefined) {
        closeCountdown();
      }
      setTimeStamps(timestamp);
    }, 100);

    function closeCountdown() {
      clearInterval(countdownInterval);
    }

    return () => {
      clearInterval(countdownInterval)
    }

  }, [auctionData])

  const memoizeCountdown = useMemo(() => {
    if (auctionData !== null && timeStamps) {
      return <TimeStampContainer timeStamps={timeStamps} />;
    } else {
      return null;
    }
  }, [auctionData, timeStamps]);

  function isLoading() {
    if(status === 'loading') return true;
    if(bids === null) return true;
    if(loadingBids === true) return true;
    if(!timeStamps) return true;
    if(status === 'unauthenticated') return 'no_user';
    return false;
  }

  return ( 
    <section id={styles.bidContainer} >
      { isLoading() === true && <div id={styles.loading}> <PuffLoader color='#0da00d' /> </div> }

      { isLoading() === 'no_user' && timeStamps.status === 'Counting Down' && <div id={styles.container}>
        { memoizeCountdown }
        <h1 style={{marginBottom: '4px'}}>{bids.length === 0 ? `Starting Bid` : 'Current Bid' }</h1>
        { bids.length === 0 ?
        <h2>${parseFloat(auctionData.price).toLocaleString()}</h2> :
        <h2>${parseFloat(getHighestBid().bid).toLocaleString()} - {getHighestBid().username}</h2>
        }
        <div>
          <button onClick={() => router.push('/account/login')}>
            Login to Bid
          </button>
        </div>
      </div> }

      { isLoading() === 'no_user' && timeStamps.status === 'Calculating winner...' && <div id={styles.container}>
        <h2>Calculating winner...</h2>
        <PacmanLoader size={10} color='white' />
      </div> }

      { isLoading() === 'no_user' && timeStamps.status === 'Auction Closed' && <div id={styles.container}>
        { bids.length !== 0 && <FontAwesomeIcon icon={faCheckCircle} color='#0da00d' size='3x' style={{marginBottom: '12px'}} /> }
        <h2 style={{textAlign: 'center'}}>{ bids.length !== 0 ? `${getHighestBid().username} won this auction for $${parseFloat(getHighestBid().bid).toLocaleString()}` : `This auction had no winner` }</h2>
      </div> }

      { isLoading() === false && timeStamps.status === 'Counting Down' && <div id={styles.container}>
        { memoizeCountdown }
        <h1 style={{marginBottom: '4px'}}>{bids.length === 0 ? `Starting Bid` : 'Current Bid' }</h1>
        { bids.length === 0 ?
        <h2>${parseFloat(auctionData.price).toLocaleString()}</h2> :
        <h2>${parseFloat(getHighestBid().bid).toLocaleString()} - {getHighestBid().username}</h2>
        }

        {!userAuthenticated() ? 
          <div>
            <button onClick={() => router.push('/account/login')}>
              Login to Bid
            </button>
          </div>
          :
          <div style={{display: auctionData.owner === session?.user?.token?.username ? 'none' : 'flex'}}>
            <NumberFormat onValueChange={(values, sourceInfo) => { !pendingBid && setUserBid(values.value)}} isAllowed={priceValueCap} decimalSeparator={false} placeholder="Max Bid" thousandSeparator={true} prefix={'$'} value={userBid} />
            <button disabled={pendingBid} onClick={submitBid}>
              { pendingBid ? <PacmanLoader size={10} color='white' /> : 'Place Bid' }
            </button>
          </div>
        }

      </div> }

      { isLoading() === false && timeStamps.status === 'Calculating winner...' && <div id={styles.container}>
        <h2>Calculating winner...</h2>
        <PacmanLoader size={10} color='white' />
      </div> }

      { isLoading() === false && timeStamps.status === 'Auction Closed' && <div id={styles.container}>
        { bids.length !== 0 && <FontAwesomeIcon icon={faCheckCircle} color='#0da00d' size='3x' style={{marginBottom: '12px'}} /> }
        <h2 style={{textAlign: 'center'}}>{ bids.length !== 0 ? `${getHighestBid().username} won this auction for $${parseFloat(getHighestBid().bid).toLocaleString()}` : `This auction had no winner` }</h2>
      </div> }

    </section>
  )
}

const TimeStampContainer = ({ timeStamps }) => {
  if(timeStamps === undefined || timeStamps === null) return;

  const noDays = timeStamps.days === 0;
  const noHours = timeStamps.hours === 0 && noDays;
  const noMinutes = timeStamps.minutes === 0 && noDays && noHours;
  const noSeconds = timeStamps.seconds === 0 && noDays && noHours && noMinutes;
  const almostOver = noDays && noHours && timeStamps.minutes <= 10;

    return timeStamps === null ?
      <section id={styles.countdown}>
        <PulseLoader size={20} color='#292929' />
      </section>
      :
      <>
        <section style={{display: timeStamps.status === 'Auction Closed' ? 'none' : 'flex'}} id={styles.countdown}>
          <div style={{display: noDays ? 'none' : 'flex'}}>
            <h1>Days</h1>
            <h2>{timeStamps.days}</h2>
          </div>
          <div style={{display: noHours ? 'none' : 'flex'}}>
            <h1>Hrs</h1>
            <h2>{timeStamps.hours}</h2>
          </div>
          <div style={{display: noMinutes ? 'none' : 'flex', backgroundColor: almostOver ? '#CA0B00' : '#0da00d'}}>
            <h1 style={{backgroundColor: almostOver ? '#CA0B00' : '#0da00d'}}>Mins</h1>
            <h2>{timeStamps.minutes}</h2>
          </div>
          <div style={{display: noSeconds ? 'none' : 'flex', backgroundColor: almostOver ? '#CA0B00' : '#0da00d'}}>
            <h1 style={{backgroundColor: almostOver ? '#CA0B00' : '#0da00d'}}>Secs</h1>
            <h2>{timeStamps.seconds}</h2>
          </div>
      </section>
    </>
  }

  function getTimeStamp(duration, created) {

    const durDays = parseInt(duration);
  
    const ending_date = new Date(Date.parse(created));
    ending_date.setDate(ending_date.getDate() + durDays);
  
    const current_day = new Date();
  
    const total = ending_date - Date.parse(current_day); // Future ending date is subtracting by current date to get proper values
    const seconds = Math.floor( (total/1000) % 60 );
    const minutes = Math.floor( (total/1000/60) % 60 );
    const hours = Math.floor( (total/(1000*60*60)) % 24 );
    const days = Math.floor( total/(1000*60*60*24) );
  
    var returnSeconds;
    var returnMinutes;
    var returnHours;
    var returnDays;
  
    if(seconds < 0) {
      returnSeconds = 0;
    } else {
      returnSeconds = seconds;
    }
  
    if(minutes < 0) {
      returnMinutes = 0;
    } else {
      returnMinutes = minutes;
    }
  
    if(hours < 0) {
      returnHours = 0;
    } else {
      returnHours = hours;
    }
  
    if(days < 0) {
      returnDays = 0;
    } else {
      returnDays = days;
    }
  
    var status = null;
  
    if(seconds <= 0 && total >= -15000) {
      status = "Calculating winner..."
    }
  
    if(total < -15000) {
      status = "Auction Closed"
    }
  
    if(total > 0) {
      status = "Counting Down"
    }
  
    return { status, days: returnDays, hours: returnHours, minutes: returnMinutes, seconds: returnSeconds }
  }

  const OptionBar = ({ listing, session, closeListing, authenticationIsOwnerCheck, handleCreateReport, reported }) => {

    const [watching, setWatching] = useState(listing?.watching?.some(watchers => session?.user?.token?.username === watchers));
    const [watchPending, setWatchPending] = useState(false);

    async function apiHandleWatch() {
        if(watchPending === true) return;
        setWatchPending(true);

        const username = session.user.token.username;
        const listingID = listing._id;

        if(watching === false) {
          const result = await axios.post(`${window.location.origin}/api/auctionHub/watch-methods/watch`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
              if(error?.response?.data?.status !== undefined) {
                  errorMsg(error?.response?.data?.status);
                  setWatchPending(false)
              } else {
                  errorMsg(error);
                  setWatchPending(false)
              }
          })
      
          if(result !== undefined) {
              if(result.status === 200) {
                  setWatching(true);
                  setWatchPending(false)
                  successMsg("Listing saved.");
              } else {
                  errorMsg('There was a problem verifying your account, please try again later.');
                  setWatchPending(false)
              }
          }
        }
  
        if(watching === true) {
          const result = await axios.post(`${window.location.origin}/api/auctionHub/watch-methods/unwatch`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
              if(error?.response?.data?.status !== undefined) {
                  errorMsg(error?.response?.data?.status);
                  setWatchPending(false)
              } else {
                  errorMsg(error);
                  setWatchPending(false)
              }
          })
      
          if(result !== undefined) {
              if(result.status === 200) {
                  setWatching(false);
                  setWatchPending(false)
                  successMsg("Listing unsaved.")
              } else {
                  errorMsg('There was a problem verifying your account, please try again later.');
                  setWatchPending(false)
              }
          }
        }
    }

    return (
        <span id={styles.optionBar}>
            <span>
                { authenticationIsOwnerCheck() && <button id={styles.reportButton} onClick={() => reported === false && handleCreateReport()}> <FontAwesomeIcon icon={faFlag} id={styles.icon} color={reported === true ? '#FFB81C' : 'white'} /> </button> }
                { authenticationIsOwnerCheck() && <button id={styles.saveButton} onClick={() => apiHandleWatch()}>

                { watchPending === false && <FontAwesomeIcon icon={faBookmark} id={styles.icon} color={watching === true ? '#FFB81C' : 'white'} /> }
                { watchPending === true && <PuffLoader size={28} color='white' /> }

                </button> }
                <button id={styles.shareButton} onClick={() => {navigator.clipboard.writeText( `${window.location.origin}/marketplace/auctions?popup=sharedPost&_id=${listing._id}` ); successMsg("Link Copied") } } > <FontAwesomeIcon icon={faShare} id={styles.icon} /> </button>
                <button id={styles.exitButton} onClick={() => closeListing(null)} > <FontAwesomeIcon icon={faXmarkCircle} id={styles.icon} /> </button>
            </span>
        </span>
    )
}

const AdvertisementSpot = ({ adID, session, router, closeListing }) => {

    const adContainerRef = useRef(null);
  
    const adInView = useInView(adContainerRef, { once: true })
    const [adShown, setAdShown] = useState(false);
    const [adData, setAdData] = useState({ img: null, url: null });
  
    useEffect(() => {
      if(adInView === false) return;
      if(adShown === true) return;
      
      async function getAdImage() {
        setAdShown(true); // Ad was in view port
        setAdData({ img: true, url: null }); // Loading State
  
        const retrieveAdImg = await axios.post(`${window.location.origin}/api/admanager/retrieveAd`, {
          adID: adID
        }).catch((error) => {
          console.error(error?.response?.data?.status || error.toString())
        })
  
        if(retrieveAdImg !== undefined) {
          if(retrieveAdImg.status === 200) {
            const img = retrieveAdImg.data.image; //img link
            const url = retrieveAdImg.data.url; //url link
            
            const structure = {
              img: img === 'NO_IMAGE' ? false : img,
              url: url
            }
  
            setAdData(structure);
          } else {
            console.error("There was an error when loading the advertisement image.");
          }
        }
      }
  
      getAdImage();
  
    }, [adInView])
  
    async function handleClick(e) {
      e.preventDefault();
      window.open(adData.url, '_blank');
  
      await axios.post(`${window.location.origin}/api/admanager/countClick`, {
        adID: adID
      }).catch((error) => {
        console.error(error.toString());
      })
    }
  
    const [coverLoaded, setCoverLoaded] = useState(false);
  
    return (
      <section ref={adContainerRef} id={styles.adSpot} style={{cursor: 'pointer', borderWidth: adData.img !== null && adData.img !== true & adData.img !== false && adData.img !== "NO_IMAGE" ? 0 : null}}>
        { adData.img === null && <h1>Ad Spot Available</h1> }
        { adData.img === false && <h1>Ad Spot Available</h1> }
        { adData.img === true && <RiseLoader size={15} color='#154734' /> }
        { (adData.img === null || adData.img === false) && session !== null && session !== undefined && <button onClick={() => { openAdManager(router); closeListing(null)} }><FontAwesomeIcon icon={faAd} id={styles.icon} /> Manager</button>}
  
        { adData.img !== null && adData.img !== true && adData.img !== false && <>
          <h6>Sponsored Advertisement</h6>
          <span style={{width: '100%', height: '100%', display: coverLoaded === true ? 'none' : 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <RiseLoader size={15} color='#154734' />
          </span>
          <Image onClick={(e) => handleClick(e)} sizes='auto auto' style={{objectFit: 'contain'}} fill src={adData.img} alt={adData.img} onLoadingComplete={() => setCoverLoaded(true)} />
        </> }
      </section>
    )
  }

  const CreateReportBox = ({ listingID, reportingUser, closeReport, successfulReport }) => {

    const { data: session, status } = useSession();

    const [pendingReport, setPendingReport] = useState(false);

    const reportTextRef = useRef(null);
    const [reportReason, setReportReason] = useState(null);

    const generateReport = async () => {
        if(listingID === null) return;
        if(pendingReport === true) return;
        if(!session) return;
        if(status !== 'authenticated') return;
        if(reportReason === null) return errorMsg("Please select a report reason.")
        if(reportTextRef === null || reportTextRef.current.value.length === 0) return errorMsg("Please enter some details about your report.");
        if(reportTextRef.current.value.length > 500) return errorMsg("Please enter some details about your report.");

        const reasonText = reportTextRef.current.value;

        setPendingReport(true);

        const result = await axios.post(`${window.location.origin}/api/TFSSystem/createReport`, {
            requestingUserID: session.user.token.id,
            origin: 'Auctions',
            reportedUser: reportingUser,
            reasonType: reportReason,
            message: reasonText,
            listingID: listingID,
            creationDate: new Date().toISOString()
          }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
          })
      
    
        if(result !== undefined) {
            if(result.status === 200) {
                closeReport();
                successfulReport();
                successMsg("Listing has been reported, thank you.");
            } else {
                errorMsg("We ran into an issue, please try again later.");
            }
        }
        setPendingReport(false);
    }

    return (
        <motion.section initial={{x: listingID === null ? '-320px' : '0'}} animate={{x: listingID === null ? '-320px' : '0', transition: { bounce: '0' }}} id={styles.reportBox}>
            <FontAwesomeIcon onClick={closeReport} icon={faXmark} id={styles.exit} />
            <h1>Create Report</h1>

            <div id={styles.types}>
                <button onClick={() => setReportReason("Inappropriate Content")} disabled={pendingReport} style={reportReason === "Inappropriate Content" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Inappropriate Content</button>
                <button onClick={() => setReportReason("Spam or Scams")} disabled={pendingReport} style={reportReason === "Spam or Scams" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Spam or Scams</button>
                <button onClick={() => setReportReason("Safety Concerns")} disabled={pendingReport} style={reportReason === "Safety Concerns" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Safety Concerns</button>
                <button onClick={() => setReportReason("Illegal Activity")} disabled={pendingReport} style={reportReason === "Illegal Activity" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Illegal Activity</button>
                <button onClick={() => setReportReason("Privacy Violation")} disabled={pendingReport} style={reportReason === "Privacy Violation" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Privacy Violation</button>
                <button onClick={() => setReportReason("Offensive Behavior")} disabled={pendingReport} style={reportReason === "Offensive Behavior" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Offensive Behavior</button>
            </div>

            <textarea maxLength={500} disabled={pendingReport} ref={reportTextRef} style={{display: reportReason !== null ? 'flex' : 'none'}} placeholder='Please give us some detail...' />
            <button disabled={pendingReport} onClick={generateReport} style={{display: reportReason !== null ? 'flex' : 'none'}}>
                {pendingReport ? <BarLoader color='white' /> : 'Submit Report'}
            </button>
        </motion.section>
    )
}

function returnRating(reviews) {
    let rating;
    let totalStars = 0;
    if(reviews?.length === 0) {
        rating = 0;
    } else {
        reviews?.forEach((review) => {
            totalStars+= review?.rating;
        })
        rating = totalStars / reviews?.length;
    }
    rating = Math.ceil(parseFloat(rating.toFixed(2)));

    return rating;
}

function createdTimeStamp(creationDate) {
    let displayTime;
    let displayTimeType;

    var delta = Math.floor(new Date().getTime() - new Date(creationDate).getTime());

    var seconds = Math.floor(delta / 1000);
    var minutes = Math.floor(seconds / 60);
    var hours =  Math.floor(minutes / 60);
    var days = Math.floor(hours / 24);
    var weeks = Math.floor(days / 7);
    var months = Math.floor(weeks / 4);

    if(seconds <= 10) {
        displayTime = "just now";
        displayTimeType = ""
    } else {
        displayTime = seconds;
        displayTimeType = "seconds ago"
    }

    if(minutes > 1) {
        displayTime = minutes;
        displayTimeType = 'minutes ago'
    }

    if(minutes === 1) {
        displayTime = minutes;
        displayTimeType = 'minute ago'
    }

    if(hours > 1) {
        displayTime = hours;
        displayTimeType = 'hours ago'
    }

    if(hours === 1) {
        displayTime = hours;
        displayTimeType = 'hour ago'
    }
    
    if(days > 1) {
        displayTime = days;
        displayTimeType = 'days ago'
    }

    if(days === 1) {
        displayTime = days;
        displayTimeType = 'day ago'
    }

    if(weeks > 1) {
        displayTime = weeks;
        displayTimeType = 'weeks ago'
    }

    if(weeks === 1) {
        displayTime = weeks;
        displayTimeType = 'week ago'
    }

    if(months > 1) {
        displayTime = months;
        displayTimeType = 'months ago'
    }

    if(months === 1) {
        displayTime = months;
        displayTimeType = 'month ago'
    }

    return {displayTime, displayTimeType}
}